import { createContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { Trie } from '../lib/trie';
import { Place } from '@/types';
import { makeSearchKey } from '@/utils/makeSearchKey';
import { formatPlaceName } from '@/lib/utils';
import { stateAbbreviations } from '@/lib/stateAbbreviations';
import { supabase } from '@/lib/supabase';

export type { Place };

// Define the shape of the context data
export interface PlaceNamesContextType {
  places: Place[];
  placeNames: string[];
  isLoading: boolean;
  error: Error | null;
  trie: Trie | null;
  placeMap: Map<string, Place>;
}

// Create the context with a default value
export const PlaceNamesContext = createContext<PlaceNamesContextType | null>(null);

// Define the props for the provider component
interface PlaceNamesProviderProps {
  children: ReactNode;
}

// Helper function to generate multiple search keys for a place
const generateSearchKeys = (placeName: string): string[] => {
  const keys = new Set<string>();
  
  // Original name key
  const originalKey = makeSearchKey(placeName);
  keys.add(originalKey);
  
  // Formatted name key
  const formattedName = formatPlaceName(placeName);
  const formattedKey = makeSearchKey(formattedName);
  keys.add(formattedKey);
  
  // Generate variations with both abbreviated and full state names
  const parts = formattedKey.split(' ');
  if (parts.length >= 2) {
    const cityPart = parts[0];
    const statePart = parts.slice(1).join(' ');
    
    // If state is abbreviated, add full state name variation
    if (statePart.length === 2) {
      const fullStateName = Object.keys(stateAbbreviations).find(
        key => stateAbbreviations[key] === statePart.toUpperCase()
      );
      if (fullStateName) {
        const fullStateKey = `${cityPart} ${fullStateName.toLowerCase()}`;
        keys.add(fullStateKey);
      }
    }
    
    // If state is full name, add abbreviated variation
    if (statePart.length > 2) {
      const abbreviation = stateAbbreviations[statePart.charAt(0).toUpperCase() + statePart.slice(1)];
      if (abbreviation) {
        const abbreviatedKey = `${cityPart} ${abbreviation.toLowerCase()}`;
        keys.add(abbreviatedKey);
      }
    }
  }
  
  return Array.from(keys);
};

// Create the provider component
export const PlaceNamesProvider = ({ children }: PlaceNamesProviderProps) => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPlaceNames = async () => {
      setIsLoading(true);
      try {
        // Check if Supabase is properly configured
        if (!supabase) {
          console.error('Supabase client is not properly configured');
          setError(new Error('Supabase client is not properly configured'));
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase.functions.invoke('find-peers', {
          body: { mode: 'all' }
        });

        if (error) {
          console.error('Error fetching place names:', error);
          throw error;
        }
        // Store original place data without formatting
        setPlaces(data);
        console.log(`FOR SEARCH: loaded ${data.length} places`);
      } catch (e) {
        console.error('Exception fetching place names:', e);
        if (e instanceof Error) {
            setError(e);
        } else {
            setError(new Error('An unknown error occurred'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlaceNames();
  }, []); // Empty dependency array ensures this runs only once on mount

  const placeNames = useMemo(() => places.map((p) => p.name), [places]);

  // Build a lookup map so any key we insert into the trie resolves to its Place.
  const placeMap = useMemo(() => {
    const map = new Map<string, Place>();

    places.forEach((place) => {
      // Generate multiple search keys for flexible searching
      const searchKeys = generateSearchKeys(place.name);
      searchKeys.forEach(key => {
        map.set(key, place);
      });

      // Key for FIPS-based search (ensure string)
      if (place.place_fips !== undefined && place.place_fips !== null) {
        const fipsKey = String(place.place_fips);
        map.set(fipsKey, place);
      }
    });

    return map;
  }, [places]);

  // Build the trie from the exact same keys present in placeMap.
  const trie = useMemo(() => {
    if (placeMap.size === 0) return null;

    const t = new Trie();
    placeMap.forEach((_place, key) => {
      if (typeof key === 'string' && key.length > 0) {
        t.insert(key);
      }
    });
    return t;
  }, [placeMap]);

  const value = { places, placeNames, isLoading, error, trie, placeMap };

  return <PlaceNamesContext.Provider value={value}>{children}</PlaceNamesContext.Provider>;
}; 